import { action, computed, makeObservable, observable, ObservableMap, runInAction } from "mobx"

import type { LcAddress } from "@/domains/core"
import type { IGauge, IGaugeRepository } from "@/domains/gauge"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { ISelectedVotePowerProvider } from "@/domains/selected-power"

import type {
  StrategyResult,
  TokenStrategyResult,
  VoteAllocation,
  VoteOptimizationInput,
} from "./vote-optimization-strategy"
import type { VoteOptimizationStrategyType } from "./vote-optimization-strategy-factory"
import { STRATEGY_TYPE, VoteOptimizationStrategyFactory } from "./vote-optimization-strategy-factory"
import { TokenFocusedVoteOptimizationStrategy } from "./strategies/token-focused-vote-optimization-strategy"

const sanitizeNumber = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0
  }
  if (value === Infinity) {
    return 0
  }
  return value
}

/**
 * StrategyManagerStore - Event-driven strategy calculation and caching
 *
 * This store is completely independent of VoteStore and other vote-related stores.
 * It listens to events only and caches strategy results until invalidated.
 *
 * Key features:
 * - Event-driven recalculation (not reactive)
 * - Caches all three strategies in a Map
 * - Clears cache on wallet connect/disconnect
 * - Recalculates all strategies when NFT selection changes
 */
export class StrategyManagerStore {
  // Map of strategy type to cached result
  private strategyResults: ObservableMap<VoteOptimizationStrategyType, StrategyResult> = observable.map()

  // Cache of estimated token amounts for dropdown display (keyed by address string)
  private tokenStrategyEstimatedCache: ObservableMap<string, number> = observable.map()

  constructor(
    private readonly gaugeRepository: IGaugeRepository,
    private readonly selectedVotePowerProvider: ISelectedVotePowerProvider,
    private readonly lockLookup: ILockLookup,
  ) {
    makeObservable(this, {
      safeStrategyResult: computed,
      degenStrategyResult: computed,
      beastStrategyResult: computed,
      allStrategyResults: computed,
      recalculateAllStrategies: action,
      clearAllStrategies: action,
    })
  }

  /**
   * Get Safe strategy result from cache
   * Returns null if not calculated yet
   */
  get safeStrategyResult(): StrategyResult | null {
    return this.strategyResults.get(STRATEGY_TYPE.SAFE) ?? null
  }

  /**
   * Get Degen strategy result from cache
   * Returns null if not calculated yet
   */
  get degenStrategyResult(): StrategyResult | null {
    return this.strategyResults.get(STRATEGY_TYPE.DEGEN) ?? null
  }

  /**
   * Get Beast strategy result from cache
   * Returns null if not calculated yet
   */
  get beastStrategyResult(): StrategyResult | null {
    return this.strategyResults.get(STRATEGY_TYPE.BEAST) ?? null
  }

  /**
   * Get all strategy results with metadata for UI
   * Returns array with id, label, estimatedValue, and distribution
   */
  get allStrategyResults(): Array<{
    id: VoteOptimizationStrategyType
    label: string
    estimatedValue: number
    distribution: Array<VoteAllocation> | null
    icon: string
  }> {
    const strategies: VoteOptimizationStrategyType[] = [STRATEGY_TYPE.SAFE, STRATEGY_TYPE.DEGEN, STRATEGY_TYPE.BEAST]
    return strategies.map((strategyType) => {
      const result = this.getStrategyResult(strategyType)
      const strategy = VoteOptimizationStrategyFactory.create(strategyType)
      // If there's no valid distribution, estimated value should be 0
      const estimatedValue = result?.distribution ? (result.estimatedValue ?? 0) : 0
      return {
        id: strategyType,
        label: strategy.getName(),
        estimatedValue,
        distribution: result?.distribution ?? null,
        icon: strategy.getIcon(),
      }
    })
  }

  /**
   * Get strategy result by type
   * Returns null if not calculated yet
   */
  getStrategyResult(strategyType: VoteOptimizationStrategyType): StrategyResult | null {
    return this.strategyResults.get(strategyType) ?? null
  }

  /**
   * Recalculate all three strategies and cache the results
   * Called by orchestrator when NFT selection changes
   */
  recalculateAllStrategies(): void {
    const gauges = this.gaugeRepository.getAll()
    const selectedVotePower = this.selectedVotePowerProvider.getSelectedVotePower()

    // If no vote power, clear cache and return
    if (selectedVotePower === 0) {
      this.strategyResults.clear()
      this.tokenStrategyEstimatedCache.clear()
      return
    }

    this.tokenStrategyEstimatedCache.clear()

    // Prepare gauge data for optimization
    const gaugeBribesForOptimization = gauges.map((gauge) => ({
      votes: gauge.votes,
      totalBribeValueUsd: gauge.totalBribeValueUsd,
      revenueSourceAddress: gauge.revenueSource?.address ?? gauge.address,
    }))

    // Calculate all three strategies
    const strategies: VoteOptimizationStrategyType[] = [STRATEGY_TYPE.SAFE, STRATEGY_TYPE.DEGEN, STRATEGY_TYPE.BEAST]
    for (const strategyType of strategies) {
      const result = this.calculateStrategyResult(strategyType, gaugeBribesForOptimization, gauges, selectedVotePower)
      this.strategyResults.set(strategyType, result)
    }
  }

  /**
   * Clear all cached strategy results
   * Called by orchestrator on wallet connect/disconnect
   */
  clearAllStrategies(): void {
    this.strategyResults.clear()
    this.tokenStrategyEstimatedCache.clear()
  }

  /**
   * Get estimated token amount for a token strategy (for dropdown display).
   * Uses same calculation as Safe/Degen/Beast but in token quantity.
   * Cached per token; invalidated when strategies are recalculated or cleared.
   */
  getTokenStrategyEstimatedAmount(tokenAddress: LcAddress): number {
    const key = tokenAddress.toString()
    const cached = this.tokenStrategyEstimatedCache.get(key)
    if (cached !== undefined) {
      return cached
    }
    const result = this.calculateTokenStrategyResult(tokenAddress)
    const amount = result?.estimatedTokenAmount ?? 0
    runInAction(() => {
      this.tokenStrategyEstimatedCache.set(key, amount)
    })
    return amount
  }

  /**
   * Calculate strategy result for a given strategy type
   * Pure function - no side effects
   */
  private calculateStrategyResult(
    strategyType: VoteOptimizationStrategyType,
    gaugeBribesForOptimization: Array<{
      votes: number
      totalBribeValueUsd: string
      revenueSourceAddress: any
    }>,
    gauges: IGauge[],
    selectedVotePower: number,
  ): StrategyResult {
    // Calculate distribution using the strategy
    const strategy = VoteOptimizationStrategyFactory.create(strategyType)
    const input: VoteOptimizationInput = {
      gaugeBribes: gaugeBribesForOptimization,
      selectedVotePower,
    }
    const distribution = strategy.calculateDistribution(input)

    // Normalize empty arrays to null for consistency
    const normalizedDistribution = distribution && distribution.length > 0 ? distribution : null

    // Calculate estimated value using the same method as VotingGauge.stats.newExpectedIncome
    // This ensures strategy estimates match the actual card estimates and total
    let estimatedValue = 0
    if (normalizedDistribution) {
      const selectedNftIds = this.selectedVotePowerProvider.getSelectedNftIds()
      const selectedLocks = selectedNftIds
        .map((id) => this.lockLookup.find(id))
        .filter((lock): lock is NonNullable<typeof lock> => lock !== undefined)

      normalizedDistribution.forEach(({ address, percentage }) => {
        const gauge = gauges.find((g) => (g.revenueSource?.address ?? g.address).toString() === address.toString())
        if (!gauge) return

        // Sum up last epoch votes for this gauge from all selected locks (same as VotingGauge)
        const totalLastEpochVotes = selectedLocks.reduce((sum, lock) => {
          const vote = lock.getLastEpochVoteForGauge(address)
          return sum + (vote?.balance ?? 0)
        }, 0)

        const myPreviousVoteCount = sanitizeNumber(totalLastEpochVotes)

        // Calculate vote count based on allocation percentage (same as VotingGauge)
        const myVoteCount = percentage > 0 ? selectedVotePower * (percentage / 100) : 0

        // Calculate new inclusive vote count (accounts for previous epoch votes)
        const totalGaugeVotes = Number(gauge.votes)
        const newInclusiveVoteCount = totalGaugeVotes + myVoteCount - myPreviousVoteCount

        // Calculate income per vote and expected income (same as VotingGauge)
        const bribesPlusFees = Number(gauge.totalBribeValueUsd)
        const newInclusiveIncomePerVote = sanitizeNumber(bribesPlusFees / newInclusiveVoteCount)
        const newExpectedIncome = sanitizeNumber(newInclusiveIncomePerVote * myVoteCount)

        estimatedValue += newExpectedIncome
      })
    }

    return {
      distribution: normalizedDistribution,
      estimatedValue,
      strategyType,
    }
  }

  /**
   * Calculate token-focused strategy result for a given token.
   * Optimizes for maximum token quantity (rewardAmount), not USD value.
   * Accounts for vote dilution. Returns null if no gauges offer the token.
   */
  calculateTokenStrategyResult(tokenAddress: LcAddress): TokenStrategyResult | null {
    const gauges = this.gaugeRepository.getAll()
    const selectedVotePower = this.selectedVotePowerProvider.getSelectedVotePower()

    if (selectedVotePower === 0) {
      return { distribution: null, estimatedTokenAmount: 0 }
    }

    const gaugesWithTokenBribes = gauges
      .map((gauge) => {
        const incentive = gauge.incentives.find((i) => i.token.address.equals(tokenAddress))
        const tokenAmount = incentive?.rewardAmount ?? 0
        if (tokenAmount === 0) return null
        return {
          gauge,
          tokenAmount,
          revenueSourceAddress: gauge.revenueSource?.address ?? gauge.address,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)

    if (gaugesWithTokenBribes.length === 0) {
      return { distribution: null, estimatedTokenAmount: 0 }
    }

    const gaugeBribesForToken = gaugesWithTokenBribes.map(({ gauge, tokenAmount, revenueSourceAddress }) => ({
      votes: gauge.votes,
      totalBribeValueUsd: String(tokenAmount),
      revenueSourceAddress,
    }))

    const strategy = new TokenFocusedVoteOptimizationStrategy()
    const input: VoteOptimizationInput = {
      gaugeBribes: gaugeBribesForToken,
      selectedVotePower,
    }
    const distribution = strategy.calculateDistribution(input)
    const normalizedDistribution = distribution && distribution.length > 0 ? distribution : null

    let estimatedTokenAmount = 0
    if (normalizedDistribution) {
      const selectedNftIds = this.selectedVotePowerProvider.getSelectedNftIds()
      const selectedLocks = selectedNftIds
        .map((id) => this.lockLookup.find(id))
        .filter((lock): lock is NonNullable<typeof lock> => lock !== undefined)

      normalizedDistribution.forEach(({ address, percentage }) => {
        const item = gaugesWithTokenBribes.find(
          ({ revenueSourceAddress }) => revenueSourceAddress.toString() === address.toString(),
        )
        if (!item) return

        const { gauge, tokenAmount } = item

        const totalLastEpochVotes = selectedLocks.reduce((sum, lock) => {
          const vote = lock.getLastEpochVoteForGauge(address)
          return sum + (vote?.balance ?? 0)
        }, 0)
        const myPreviousVoteCount = sanitizeNumber(totalLastEpochVotes)
        const myVoteCount = percentage > 0 ? selectedVotePower * (percentage / 100) : 0
        const totalGaugeVotes = Number(gauge.votes)
        const newInclusiveVoteCount = totalGaugeVotes + myVoteCount - myPreviousVoteCount

        const tokensPerVote = sanitizeNumber(tokenAmount / newInclusiveVoteCount)
        const expectedTokens = sanitizeNumber(tokensPerVote * myVoteCount)
        estimatedTokenAmount += expectedTokens
      })
    }

    return {
      distribution: normalizedDistribution,
      estimatedTokenAmount,
    }
  }
}
